// Adapters: live Fleet API → the dashboard's documented contract.
//
// `src/types/fleet.ts` is the contract the dashboard was built against
// (snake_case, nested `region`). The live backend serves camelCase with a
// flat region and a few renamed fields. These functions bridge the gap so
// the UI components and types stay unchanged — the mismatch is absorbed in
// exactly one place.

import type {
  FleetEvent,
  FleetHealth,
  FleetSummary,
  Paginated,
  ServerDetail,
  ServerListItem,
} from '@/types/fleet';
import type { Playbook, SurveyField } from '@/types/provisioning';

// ─── Live API response shapes (camelCase, as the backend actually serves) ───

export interface ApiServerListItem {
  name: string;
  role: string;
  publicIp: string;
  privateIp: string | null;
  provider: string;
  regionContinent: string;
  regionCountry: string;
  regionCity: string;
  status: string;
  environment?: string;
  lastSeenAt: string | null;
  createdAt: string;
  dnsHostname: string | null;
  /** Capability tags this server advertises. Backend added on 2026-05-30. */
  capabilities: string[];
}

export interface ApiServerList {
  items: ApiServerListItem[];
  page: number;
  pageSize: number;
  total: number;
}

export interface ApiSummary {
  counts: {
    byRole: Record<string, number>;
    byStatus: Record<string, number>;
    byProvider: Record<string, number>;
    byRegion: Record<string, number>;
  };
  heartbeats: { freshWindowSeconds: number; fresh: number; total: number };
  filters: FleetSummary['filters'];
}

export interface ApiHealth {
  status: string;
  dbConnected: number | boolean;
  serversSeenRecently: number;
  heartbeatLostLastHour: number;
  checkedAt: string;
}

// ─── Adapters ───────────────────────────────────────────────────────────────

/** GET /fleet/servers → Paginated<ServerListItem>. */
export function adaptServerList(api: ApiServerList): Paginated<ServerListItem> {
  return {
    items: api.items.map((s, index) => ({
      // The list endpoint omits a numeric id; rows are keyed/linked by name.
      id: index + 1,
      name: s.name,
      role: s.role as ServerListItem['role'],
      public_ip: s.publicIp,
      provider: s.provider,
      region: {
        continent: s.regionContinent,
        country: s.regionCountry,
        city: s.regionCity,
      },
      status: s.status as ServerListItem['status'],
      environment: s.environment ?? 'production',
      last_seen_at: s.lastSeenAt,
      dns_hostname: s.dnsHostname ?? null,
      protocols: [],
      capabilities: (s.capabilities ?? []) as ServerListItem['capabilities'],
    })),
    total: api.total,
    page: api.page,
    page_size: api.pageSize,
  };
}

/** GET /fleet/summary → FleetSummary. (`byRegion` carries the region counts the
 *  "Regions" card renders.) */
export function adaptSummary(api: ApiSummary): FleetSummary {
  return {
    counts: {
      by_role: api.counts.byRole,
      by_status: api.counts.byStatus,
      by_provider: api.counts.byProvider,
      by_continent: api.counts.byRegion,
    },
    heartbeats: {
      fresh_window_seconds: api.heartbeats.freshWindowSeconds,
      fresh: api.heartbeats.fresh,
      total: api.heartbeats.total,
    },
    filters: api.filters,
  };
}

export interface ApiFleetEvent {
  id: number;
  eventType: string;
  source: string;
  payload: Record<string, unknown> | null;
  occurredAt: string;
}

/** Live event → contract FleetEvent. */
function adaptEvent(e: ApiFleetEvent): FleetEvent {
  return {
    id: e.id,
    event_type: e.eventType as FleetEvent['event_type'],
    source: e.source,
    payload: e.payload ?? {},
    occurred_at: e.occurredAt,
  };
}

export interface ApiAssociation {
  upstreamName?: string;
  downstreamName?: string;
  tunnelProto: string;
  tunnelEndpoint: string;
}

export interface ApiServerDetail extends ApiServerListItem {
  dnsHostname: string | null;
  providerAccount: string | null;
  vmType: string | null;
  cpuCores: number | null;
  memoryGb: number | null;
  diskGb: number | null;
  bandwidthTb: number | null;
  cloudflareTunnelId: string | null;
  commissionedAt: string | null;
  decommissionedAt: string | null;
  decommissionReason: string | null;
  currentImageVersions: Record<string, string> | null;
  notes: string | null;
  updatedAt: string;
  protocols: { protocol: string; port: number | null; enabled: boolean }[];
  capabilities: string[];
  upstreamAssociations: ApiAssociation[];
  downstreamAssociations: ApiAssociation[];
  recentEvents: ApiFleetEvent[];
}

/** GET /fleet/servers/:name → ServerDetail. */
export function adaptServerDetail(api: ApiServerDetail): ServerDetail {
  return {
    id: 0,
    name: api.name,
    role: api.role as ServerDetail['role'],
    public_ip: api.publicIp,
    provider: api.provider,
    region: {
      continent: api.regionContinent,
      country: api.regionCountry,
      city: api.regionCity,
    },
    status: api.status as ServerDetail['status'],
    environment: api.environment ?? 'production',
    last_seen_at: api.lastSeenAt,
    protocols: (api.protocols ?? []) as ServerDetail['protocols'],
    private_ip: api.privateIp,
    dns_hostname: api.dnsHostname,
    provider_account: api.providerAccount,
    vm_type: api.vmType,
    cpu_cores: api.cpuCores,
    memory_gb: api.memoryGb,
    disk_gb: api.diskGb,
    bandwidth_tb: api.bandwidthTb,
    cloudflare_tunnel_id: api.cloudflareTunnelId,
    commissioned_at: api.commissionedAt,
    decommissioned_at: api.decommissionedAt,
    decommission_reason: api.decommissionReason,
    current_image_versions: api.currentImageVersions,
    notes: api.notes,
    capabilities: (api.capabilities ?? []) as ServerDetail['capabilities'],
    associations: {
      upstream: (api.upstreamAssociations ?? []).map((a) => ({
        name: a.upstreamName ?? '',
        tunnel_proto: a.tunnelProto,
        tunnel_endpoint: a.tunnelEndpoint,
      })),
      downstream: (api.downstreamAssociations ?? []).map((a) => ({
        name: a.downstreamName ?? '',
        tunnel_proto: a.tunnelProto,
        tunnel_endpoint: a.tunnelEndpoint,
      })),
    },
    events: (api.recentEvents ?? []).map(adaptEvent),
    created_at: api.createdAt,
    updated_at: api.updatedAt,
  };
}

/** GET /fleet/health → FleetHealth. */
export function adaptHealth(api: ApiHealth): FleetHealth {
  return {
    status: api.status === 'ok' ? 'healthy' : 'degraded',
    db_ok: Boolean(api.dbConnected),
    last_seen_at: null,
    heartbeat_lost_last_hour: api.heartbeatLostLastHour,
  };
}

export interface ApiEventPage {
  items: ApiFleetEvent[];
  page: number;
  pageSize: number;
  total: number;
}

/** GET /fleet/servers/:name/events → Paginated<FleetEvent>. */
export function adaptEventPage(api: ApiEventPage): Paginated<FleetEvent> {
  return {
    items: (api.items ?? []).map(adaptEvent),
    page: api.page,
    page_size: api.pageSize,
    total: api.total,
  };
}

interface ApiSurveyField {
  name: string;
  title: string;
  type: string;
  required: number | boolean;
  default?: string;
}

export interface ApiPlaybook {
  id: number;
  name: string;
  group: string;
  tier: string;
  description: string | null;
  survey: ApiSurveyField[];
}

/** GET /provisioning/playbooks → Playbook[]. The live API serves `required`
 *  as 1/0; the contract expects a boolean. */
export function adaptPlaybooks(api: ApiPlaybook[]): Playbook[] {
  return (api ?? []).map((p) => ({
    id: p.id,
    name: p.name,
    group: p.group,
    tier: p.tier as Playbook['tier'],
    description: p.description,
    survey: (p.survey ?? []).map(
      (f): SurveyField => ({
        name: f.name,
        title: f.title,
        type: f.type === 'password' ? 'password' : 'text',
        required: Boolean(f.required),
        default: f.default,
      }),
    ),
  }));
}
